import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { hasFreeAnonymousHints, getHintAuthMessage } from '../utils/anonymousHints';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { DndContext, useSensor, useSensors, MouseSensor, TouchSensor, closestCenter, useDraggable, useDroppable, DragOverlay } from "@dnd-kit/core";
import { GameShell, useAiutiniModal } from "./layout";
import { GameEndSection, LivesDisplay } from "./game";
import Button from "./Button";
import SnackAlert from "./SnackAlert";
import { SpettriSkeleton } from './Skeleton';
import styles from "../styles/Spettri.module.css";
import modalStyles from "../styles/modal.module.css";
import gameStyles from "../styles/GameStyles.module.css";
import { getGameConfig, getGameMessage } from "../games";
import { buildTrackedAuthRedirectUrl, logGameEvent, trackGameHintInsufficientBalance } from "../analytics";
import { useGettoncini } from "../contexts/GettonciniContext";
import { hasGameHistory } from "../util";
import useHasEverAuthenticated from "../hooks/useHasEverAuthenticated";
import { useSpettriEvents } from "../hooks/games/useSpettriEvents";
import AIHelperTrigger from './ai-helper/AIHelperTrigger';
import GettonciniCostChip from './GettonciniCostChip';
import RewardedGettonciniCard from './RewardedGettonciniCard';
import { getSpettriMobileGameplayAdEnabled } from './spettriMobileGameplayAd';
import { usePuzzleInstances } from '../contexts/PuzzleInstancesContext';
import FaIcon from './FaIcon';
import { getSpettriItemsPerSpectrum } from '../gameVariants';
import { getDisabledHintCount, getDisabledHintsMessage } from '../utils/aiutiniModal';

const INSUFFICIENT_HINT_BALANCE_MESSAGE = "Gettoncini insufficienti per questo aiutino.";

const getSpettriInstructionItemsPerSpectrum = (config) => (
  config?.rules_version === 1 ? 4 : 5
);

const canAffordHintCost = ({ cost, balance, isPreview = false, isFree = false }) => {
  if (isPreview || isFree) return true;
  if (typeof cost !== "number" || typeof balance !== "number") return true;
  return balance >= cost;
};

/**
 * IMPORTANT: All state is stored in BACKEND ORDER (index 0 = lowest value, last index = highest value).
 * We only reverse arrays when RENDERING to show highest at top.
 * 
 * Backend order: [lowest, ..., highest]
 * Display order: [highest, ..., lowest] (visual top to bottom)
 * 
 * Conversion: displayIdx = itemsPerSpectrum - 1 - backendIdx
 *             backendIdx = itemsPerSpectrum - 1 - displayIdx
 */

const SpettriGame = ({
  date,
  gameConfig: providedGameConfig,
  puzzleConfig,
  sessionState,
  solutions: hookSolutions,
  status,
  gameOver,
  won,
  justWon,
  justLost = false,
  isLoading,
  sessionError,
  isEventPending,
  dispatchEvent,
  dispatchHintEvent,
  hintAllowance = null,
  refreshHintAllowance = null,
  updateLocalState,
  isSignedIn = false,
  isPreview = false,
  previewSponsorship = null,
  showNavigation = true,
  showGameHeader = true,
  previewEndLabel,
}) => {
  const hasFreeHints = hasFreeAnonymousHints(hintAllowance, 'spettri', isSignedIn);
  const navigate = useNavigate();
  const location = useLocation();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const gameConfig = providedGameConfig || getGameConfig("spettri");
  const effectiveDate = date || "preview";
  const shouldTrack = !isPreview;
  const itemsPerSpectrum = getSpettriItemsPerSpectrum(puzzleConfig);
  const instructionItemsPerSpectrum = getSpettriInstructionItemsPerSpectrum(puzzleConfig);

  // Get puzzle instance ID for AI helper
  const { getPuzzleInstanceId, getPuzzleInstance } = usePuzzleInstances();
  const puzzleInstanceId = getPuzzleInstanceId(gameConfig.id, effectiveDate);
  const sponsorship = previewSponsorship || (isPreview ? null : getPuzzleInstance(gameConfig.id, effectiveDate)?.sponsorship || null);

  // Gettoncini context for hint costs display
  const { balance, getHintCost } = useGettoncini();

  const ERROR_MESSAGES = {
    INVALID_SUBMISSION: "Errore durante l'invio della risposta",
    GAME_LOAD_ERROR: "Errore nel caricamento del gioco",
    SUBMIT_ERROR: "Errore durante l'invio",
    INCOMPLETE: "Completa entrambe le progressioni prima di inviare",
  };

  const {
    isModalOpen,
    modalTitle,
    modalContent,
    closeModal,
    showModal,
    showAiutiniModal,
    refreshAiutiniModal,
    isAiutiniModalOpen,
  } = useAiutiniModal();

  useEffect(() => {
    if (isAiutiniModalOpen) refreshHintAllowance?.();
  }, [isAiutiniModalOpen, refreshHintAllowance]);

  // Spettri-specific event helpers
  const { submitArrangement, revealTitles, arrangeColumns } = useSpettriEvents(dispatchEvent, dispatchHintEvent);

  // Local UI state - spectra arrangements stored in BACKEND ORDER (index 0 = lowest)
  const [spectrum0, setSpectrum0] = useState([]);
  const [spectrum1, setSpectrum1] = useState([]);
  const [draggingItem, setDraggingItem] = useState(null);
  const [submitCooldown, setSubmitCooldown] = useState(false);
  const [isAnimatingReveal, setIsAnimatingReveal] = useState(false);
  const [flippingItems, setFlippingItems] = useState(new Set()); // Uses backend indices
  const [swappedItems, setSwappedItems] = useState(new Set()); // Uses backend indices
  const [showEndContent, setShowEndContent] = useState(false);
  const [solutions, setSolutions] = useState(null);
  const [endSectionAnchor, setEndSectionAnchor] = useState(null);
  const gameplayControlsRef = useRef(null);

  // Loading state for hints
  const [loadingHint, setLoadingHint] = useState(null);
  const [aiutiniError, setAiutiniError] = useState("");
  const isSubmitPending = isEventPending && loadingHint === null;
  
  // Local state for revealed titles (set from hint response)
  const [localRevealedTitles, setLocalRevealedTitles] = useState(null);
  
  // When true, we're showing the final correct solution (after loss) - skip incorrect feedback styling
  const [showingSolution, setShowingSolution] = useState(false);
  
  // Track items that have been revealed during the loss animation (uses backend indices)
  const [revealedItems, setRevealedItems] = useState(new Set());
  
  // Track if game was already over when component mounted (to distinguish returning vs just lost)
  const wasGameOverOnMount = useRef(null);
  
  // Track if handleGameOverLoss has been called (means game just ended, don't show solution immediately)
  const lossAnimationScheduled = useRef(false);
  const aiutiniModalSignatureRef = useRef("");
  const handleRevealThemesRef = useRef(handleRevealThemes);
  const handleArrangeColumnsRef = useRef(handleArrangeColumns);

  const snackAlertRef = useRef();

  // Derive state from session - all in BACKEND ORDER
  const lives = sessionState?.lives ?? 5;
  const completed = won;
  const feedback = sessionState?.feedback || null; // Backend order
  const fixed = puzzleConfig?.fixed || null; // Backend order: [[spectrum_idx, item_idx], ...]
  
  // Use local state first, then fall back to session state
  const revealedTitles = localRevealedTitles || sessionState?.revealed_titles || null;
  const revealThemesUsed = sessionState?.hints_used?.reveal_titles || false;
  const arrangeColumnsUsed = sessionState?.hints_used?.arrange_columns || false;

  // Helper: check if an item at backend index is fixed
  const isFixed = useCallback((spectrumIdx, backendIdx) => {
    if (!fixed) return false;
    // Handle both formats: [spectrum_idx, item_idx] or [[s1, i1], [s2, i2]]
    if (Array.isArray(fixed) && fixed.length === 2 && typeof fixed[0] === 'number') {
      // Legacy format: [spectrum_idx, item_idx]
      return fixed[0] === spectrumIdx && fixed[1] === backendIdx;
    }
    // New format: [[s1, i1], [s2, i2]]
    return fixed.some(([fs, fi]) => fs === spectrumIdx && fi === backendIdx);
  }, [fixed]);

  // Helper: check if item at backend index is correct (from feedback)
  const isCorrect = useCallback((spectrumIdx, backendIdx) => {
    if (!feedback) return false;
    return feedback[spectrumIdx]?.[backendIdx]?.correct === true;
  }, [feedback]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 3 } }),
    useSensor(TouchSensor, { pressDelay: 250, pressTolerance: 12 })
  );

  const collisionDetection = useCallback((args) => {
    return closestCenter(args);
  }, []);

  // Initialize spectra from session state (already in backend order)
  useEffect(() => {
    if (!puzzleConfig || isLoading) return;

    // Track if game was already over on first load (before any other logic)
    const isFirstLoad = wasGameOverOnMount.current === null;
    if (isFirstLoad) {
      wasGameOverOnMount.current = gameOver;
    }

    // Reset animation state for new game
    setIsAnimatingReveal(false);
    setFlippingItems(new Set());

    // Initialize spectra from session state (backend order)
    // But DON'T update spectra if game just ended during this session - 
    // the backend returns the solution and we want to animate revealing it
    const gameJustEnded = gameOver && !wasGameOverOnMount.current;
    if (sessionState?.spectrum_0 && sessionState?.spectrum_1 && !gameJustEnded) {
      setSpectrum0(sessionState.spectrum_0);
      setSpectrum1(sessionState.spectrum_1);
    }

    // Only show solution immediately if:
    // 1. Game is over AND
    // 2. It was already over when component mounted (user returning) AND
    // 3. Loss animation hasn't been scheduled (not a fresh loss during this session)
    // This handles the case where the component re-mounts after a loss - we check
    // lossAnimationScheduled to know if handleGameOverLoss was already called.
    if (gameOver && wasGameOverOnMount.current && !lossAnimationScheduled.current) {
      setShowEndContent(true);
      setShowingSolution(true); // Game is over, show items as correct
      // Use solutions from hook (from get_solutions_config) for reload cases
      if (hookSolutions && hookSolutions.length >= 2) {
        setSolutions(hookSolutions);
      }
    }
    
    // If titles were revealed via hint, set local state
    if (sessionState?.revealed_titles) {
      setLocalRevealedTitles(sessionState.revealed_titles);
    }

    // Show instructions for first-time players
    if (!isPreview && !hasGameHistory(gameConfig.id)) {
      showModal({
        title: "Istruzioni",
        content: <Instructions itemsPerSpectrum={instructionItemsPerSpectrum} />
      });
      if (shouldTrack) {
        logGameEvent('instructions_viewed', gameConfig.id, effectiveDate, { first_time: true });
      }
    }
  }, [puzzleConfig, isLoading, gameOver, hookSolutions]);

  // Redirect to auth page for unauthenticated users
  const redirectToAuth = useCallback((allowanceOverride = null) => {
    if (isPreview) return;
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: (hasEverAuthenticated || hintAllowance?.eligible === false) ? 'sign_in' : 'sign_up',
      source_surface: 'game_hints',
      gate_reason: 'hints_requires_auth',
      next_path: `${location.pathname}${location.search || ''}${location.hash || ''}`,
      message: getHintAuthMessage(allowanceOverride?.game_slug === gameConfig.id ? allowanceOverride : hintAllowance),
      is_returning_device: hasEverAuthenticated,
      game_slug: gameConfig?.id || null,
      track_gate_viewed: true,
      trigger: 'hint_click_redirect',
    });
    navigate(authUrl);
  }, [gameConfig?.id, hasEverAuthenticated, location.pathname, location.search, navigate, isPreview, hintAllowance, location.hash]);

  // Get hint costs for display
  const hintCosts = useMemo(() => ({
    reveal_titles: getHintCost('spettri', 'reveal_titles'),
    arrange_columns: getHintCost('spettri', 'arrange_columns')
  }), [getHintCost]);
  const hintAffordability = useMemo(() => ({
    reveal_titles: canAffordHintCost({ cost: hintCosts.reveal_titles, balance, isPreview, isFree: hasFreeHints }),
    arrange_columns: canAffordHintCost({ cost: hintCosts.arrange_columns, balance, isPreview, isFree: hasFreeHints }),
  }), [balance, hintCosts.arrange_columns, hintCosts.reveal_titles, isPreview, hasFreeHints]);
  handleRevealThemesRef.current = handleRevealThemes;
  handleArrangeColumnsRef.current = handleArrangeColumns;

  // Update Aiutini modal content when loadingHint changes
  useEffect(() => {
    if (!isAiutiniModalOpen) {
      aiutiniModalSignatureRef.current = "";
      setAiutiniError("");
      return;
    }

    const nextSignature = JSON.stringify({
      loadingHint: loadingHint || "",
      aiutiniError,
      revealUsed: !!revealThemesUsed,
      arrangeUsed: !!arrangeColumnsUsed,
      disabled: completed || gameOver || isAnimatingReveal,
      revealTitlesCost: hintCosts.reveal_titles ?? null,
      arrangeColumnsCost: hintCosts.arrange_columns ?? null,
      revealTitlesAffordable: !!hintAffordability.reveal_titles,
      arrangeColumnsAffordable: !!hintAffordability.arrange_columns,
      isSignedIn,
      hintAllowance,
      hasFreeHints,
      hasEverAuthenticated,
    });

    if (aiutiniModalSignatureRef.current === nextSignature) {
      return;
    }
    aiutiniModalSignatureRef.current = nextSignature;

    refreshAiutiniModal(
      <Aiutini
        revealUsed={revealThemesUsed}
        arrangeUsed={arrangeColumnsUsed}
        onReveal={() => handleRevealThemesRef.current?.()}
        onArrange={() => handleArrangeColumnsRef.current?.()}
        disabled={completed || gameOver || isAnimatingReveal}
        hintCosts={hintCosts}
        hintAffordability={hintAffordability}
        loadingHint={loadingHint}
        errorMessage={aiutiniError}
        isSignedIn={isSignedIn}
        hasFreeHints={hasFreeHints}
        isPreview={isPreview}
        onAuthRedirect={redirectToAuth}
        hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
        balance={balance}
      />,
      "Aiutini",
    );
  }, [aiutiniError, arrangeColumnsUsed, completed, gameOver, hasEverAuthenticated, hintAffordability, hintCosts, isAiutiniModalOpen, isAnimatingReveal, isSignedIn, hasFreeHints, loadingHint, redirectToAuth, refreshAiutiniModal, revealThemesUsed, isPreview, hintAllowance]);

  // Convert display index to backend index
  const toBackendIdx = (displayIdx) => itemsPerSpectrum - 1 - displayIdx;

  function onDragStart(event) {
    if (completed || gameOver || isAnimatingReveal) return;
    const data = event?.active?.data?.current;
    if (data) {
      const { spectrumIdx, displayIdx } = data;
      const backendIdx = toBackendIdx(displayIdx);
      
      // Don't allow dragging fixed or correct items
      if (isFixed(spectrumIdx, backendIdx) || isCorrect(spectrumIdx, backendIdx)) return;
      
      setDraggingItem(data);
    }
  }

  function onDragEnd(event) {
    const { active, over } = event || {};
    setDraggingItem(null);
    
    if (!active || !over || completed || gameOver || isAnimatingReveal) return;

    const sourceData = active.data?.current;
    const targetData = over.data?.current;

    if (!sourceData || !targetData) return;

    const { spectrumIdx: sourceSpectrum, displayIdx: sourceDisplayIdx } = sourceData;
    const { spectrumIdx: targetSpectrum, displayIdx: targetDisplayIdx } = targetData;
    
    const sourceBackendIdx = toBackendIdx(sourceDisplayIdx);
    const targetBackendIdx = toBackendIdx(targetDisplayIdx);

    // Can't swap with fixed items
    if (isFixed(sourceSpectrum, sourceBackendIdx) || isFixed(targetSpectrum, targetBackendIdx)) return;

    // Can't swap with correct items
    if (isCorrect(sourceSpectrum, sourceBackendIdx) || isCorrect(targetSpectrum, targetBackendIdx)) return;

    // Handle swaps (using backend indices)
    if (sourceSpectrum === targetSpectrum) {
      // Swap within the same spectrum
      if (sourceSpectrum === 0) {
        setSpectrum0(prev => {
          const next = [...prev];
          [next[sourceBackendIdx], next[targetBackendIdx]] = [next[targetBackendIdx], next[sourceBackendIdx]];
          return next;
        });
      } else {
        setSpectrum1(prev => {
          const next = [...prev];
          [next[sourceBackendIdx], next[targetBackendIdx]] = [next[targetBackendIdx], next[sourceBackendIdx]];
          return next;
        });
      }
    } else {
      // Swap across spectra
      const sourceItem = sourceSpectrum === 0 ? spectrum0[sourceBackendIdx] : spectrum1[sourceBackendIdx];
      const targetItem = targetSpectrum === 0 ? spectrum0[targetBackendIdx] : spectrum1[targetBackendIdx];
      
      if (sourceSpectrum === 0) {
        setSpectrum0(prev => {
          const next = [...prev];
          next[sourceBackendIdx] = targetItem;
          return next;
        });
        setSpectrum1(prev => {
          const next = [...prev];
          next[targetBackendIdx] = sourceItem;
          return next;
        });
      } else {
        setSpectrum1(prev => {
          const next = [...prev];
          next[sourceBackendIdx] = targetItem;
          return next;
        });
        setSpectrum0(prev => {
          const next = [...prev];
          next[targetBackendIdx] = sourceItem;
          return next;
        });
      }
    }

    // Clear feedback only for the swapped positions (feedback becomes stale for moved items)
    if (feedback) {
      const newFeedback = [
        feedback[0]?.map((f, idx) => {
          if ((sourceSpectrum === 0 && idx === sourceBackendIdx) || 
              (targetSpectrum === 0 && idx === targetBackendIdx)) {
            return null;
          }
          return f;
        }),
        feedback[1]?.map((f, idx) => {
          if ((sourceSpectrum === 1 && idx === sourceBackendIdx) || 
              (targetSpectrum === 1 && idx === targetBackendIdx)) {
            return null;
          }
          return f;
        })
      ];
      updateLocalState?.({ feedback: newFeedback });
    }

    // Mark swapped items for a short visual flash (using backend indices)
    const newSwapped = new Set(swappedItems);
    newSwapped.add(`${sourceSpectrum}-${sourceBackendIdx}`);
    newSwapped.add(`${targetSpectrum}-${targetBackendIdx}`);
    setSwappedItems(newSwapped);
    setTimeout(() => {
      setSwappedItems(prev => {
        const next = new Set(prev);
        next.delete(`${sourceSpectrum}-${sourceBackendIdx}`);
        next.delete(`${targetSpectrum}-${targetBackendIdx}`);
        return next;
      });
    }, 1500);
  }

  async function handleRevealThemes() {
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!hintAffordability.reveal_titles) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_reveal_titles',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.reveal_titles,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }
    
    setAiutiniError("");
    setLoadingHint('reveal_titles');
    try {
      const response = await revealTitles(redirectToAuth);
      
      if (!response.success) {
        if (response.error === 'INSUFFICIENT_BALANCE') {
          setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
        } else if (response.error === 'ALREADY_USED') {
          setAiutiniError("Aiutino gia usato");
        } else if (response.error === 'NOT_AUTHENTICATED') {
          return;
        } else {
          setAiutiniError(response.message || "Errore durante la richiesta");
        }
        return;
      }
      
      const titles = response.hint_data?.titles || response.result?.titles || response.state?.revealed_titles;
      if (Array.isArray(titles) && titles.length === 2) {
        setLocalRevealedTitles(titles);
      }
      
      snackAlertRef.current?.showSnackAlert("Temi rivelati!", "success", 2000);
      closeModal();
    } catch (e) {
      console.error('Reveal titles hint failed:', e);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  }

  async function handleArrangeColumns() {
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!hintAffordability.arrange_columns) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_arrange_columns',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.arrange_columns,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }
    
    setAiutiniError("");
    setLoadingHint('arrange_columns');
    try {
      const response = await arrangeColumns(redirectToAuth);
      
      if (!response.success) {
        if (response.error === 'INSUFFICIENT_BALANCE') {
          setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
        } else if (response.error === 'ALREADY_USED') {
          setAiutiniError("Aiutino gia usato");
        } else if (response.error === 'NOT_AUTHENTICATED') {
          return;
        } else {
          setAiutiniError(response.message || "Errore durante la richiesta");
        }
        return;
      }
      
      // Backend updates the spectra directly in the state (backend order)
      if (response.state?.spectrum_0 && response.state?.spectrum_1) {
        setSpectrum0(response.state.spectrum_0);
        setSpectrum1(response.state.spectrum_1);
      }

      snackAlertRef.current?.showSnackAlert("Elementi nelle colonne corrette!", "success", 2000);
      closeModal();
    } catch (e) {
      console.error('Arrange columns hint failed:', e);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  }

  async function handleSubmit() {
    if (isSubmitPending || submitCooldown || completed || gameOver || isAnimatingReveal) return;

    if (!spectrum0.length || !spectrum1.length) {
      snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.INCOMPLETE, "error");
      return;
    }

    setSubmitCooldown(true);
    try {
      // spectrum0 and spectrum1 are already in backend order, send directly
      const response = await submitArrangement(spectrum0, spectrum1);

      if (response.success) {
        const result = response.result;
        
        if (result?.complete) {
          snackAlertRef.current?.showSnackAlert("Tutto corretto!", "success", 2000);
          if (shouldTrack) {
            logGameEvent('guess_correct', gameConfig.id, effectiveDate);
          }
          
          if (result.answers) {
            setSolutions(result.answers);
          }
          setShowingSolution(true); // Game won, all items are correct
          setShowEndContent(true);
        } else {
          if (shouldTrack) {
            logGameEvent('guess_incorrect', gameConfig.id, effectiveDate);
          }
          
          if (response.state?.lives === 0) {
            // Set flag IMMEDIATELY to prevent useEffect from showing solution
            // This must happen before React processes any state updates
            lossAnimationScheduled.current = true;
            handleGameOverLoss(result?.answers);
          } else {
            snackAlertRef.current?.showSnackAlert("Ci sono errori: ritenta.", "error", 2000);
          }
        }
      } else {
        snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.SUBMIT_ERROR, "error", 1500);
      }
    } catch (err) {
      console.error("Submit error:", err);
      snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.SUBMIT_ERROR, "error", 1500);
    } finally {
      setTimeout(() => setSubmitCooldown(false), 1500);
    }
  }

  async function handleGameOverLoss(answers) {
    // Mark that loss animation is scheduled - prevents useEffect from showing solution immediately
    lossAnimationScheduled.current = true;
    
    snackAlertRef.current?.showSnackAlert("Partita finita", "error", 2000);

    if (answers) {
      setSolutions(answers);
      setTimeout(() => {
        animateReveal(answers);
      }, 2500);
    } else {
      setTimeout(() => {
        setShowEndContent(true);
      }, 2000);
    }
  }

  async function animateReveal(spectra) {
    setIsAnimatingReveal(true);
    // Don't set showingSolution yet - we want to show incorrect feedback first
    
    // Get correct solutions (already in backend order)
    const correctSpectrum0 = spectra[0].items;
    const correctSpectrum1 = spectra[1].items;
    
    // Build list of backend positions that need fixing
    const itemsToFix = [];
    
    for (let backendIdx = 0; backendIdx < itemsPerSpectrum; backendIdx++) {
      if (spectrum0[backendIdx] !== correctSpectrum0[backendIdx]) {
        itemsToFix.push({ spectrumIdx: 0, backendIdx, correctItem: correctSpectrum0[backendIdx] });
      }
    }
    
    for (let backendIdx = 0; backendIdx < itemsPerSpectrum; backendIdx++) {
      if (spectrum1[backendIdx] !== correctSpectrum1[backendIdx]) {
        itemsToFix.push({ spectrumIdx: 1, backendIdx, correctItem: correctSpectrum1[backendIdx] });
      }
    }
    
    // Animate each item flip sequentially
    for (const item of itemsToFix) {
      const { spectrumIdx, backendIdx, correctItem } = item;
      const itemKey = `${spectrumIdx}-${backendIdx}`;
      
      setFlippingItems(prev => new Set(prev).add(itemKey));
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (spectrumIdx === 0) {
        setSpectrum0(prev => {
          const next = [...prev];
          next[backendIdx] = correctItem;
          return next;
        });
      } else {
        setSpectrum1(prev => {
          const next = [...prev];
          next[backendIdx] = correctItem;
          return next;
        });
      }
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setFlippingItems(prev => {
        const next = new Set(prev);
        next.delete(itemKey);
        return next;
      });
      
      // Mark this item as revealed (now showing correct answer)
      setRevealedItems(prev => new Set(prev).add(itemKey));
      
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setIsAnimatingReveal(false);
    setShowingSolution(true); // Now all items should show as correct
    
    setTimeout(() => {
      setShowEndContent(true);
    }, 800);
  }

  const handleClosePreview = useCallback(() => {
    window.close();
  }, []);

  const isGameplayAdActive = getSpettriMobileGameplayAdEnabled({
    completed,
    gameOver,
    isAnimatingReveal,
  });
  const mobileGameplayAd = useMemo(() => ({
    enabled: isGameplayAdActive || !!endSectionAnchor,
    anchors: endSectionAnchor ? [endSectionAnchor] : [gameplayControlsRef],
    placement: `game-shell-${gameConfig.id}-mobile-gameplay-banner`,
    testId: `${gameConfig.id}-mobile-gameplay-banner`,
  }), [endSectionAnchor, gameConfig.id, isGameplayAdActive]);

  // Build header actions
  const headerActions = [
    {
      icon: "fa fa-question-circle",
      title: "Istruzioni",
      onClick: () => {
        if (shouldTrack) {
          logGameEvent("instructions_viewed", gameConfig.id, effectiveDate, { first_time: false });
        }
        showModal({
          title: "Istruzioni",
          content: <Instructions itemsPerSpectrum={instructionItemsPerSpectrum} />,
        });
      },
    },
    {
      icon: "fa fa-medkit",
      title: "Aiutini",
      onClick: () => {
        setAiutiniError("");
        if (shouldTrack) {
          logGameEvent('help_viewed', gameConfig.id, effectiveDate);
        }
        showAiutiniModal(
          <Aiutini 
            revealUsed={revealThemesUsed}
            arrangeUsed={arrangeColumnsUsed}
            onReveal={() => handleRevealThemesRef.current?.()}
            onArrange={() => handleArrangeColumnsRef.current?.()}
            disabled={completed || gameOver || isAnimatingReveal}
            hintCosts={hintCosts}
            hintAffordability={hintAffordability}
            loadingHint={loadingHint}
            errorMessage=""
            isSignedIn={isSignedIn}
            hasFreeHints={hasFreeHints}
            onAuthRedirect={redirectToAuth}
            hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
            isPreview={isPreview}
            balance={balance}
          />,
          "Aiutini",
        );
      }
    },
  ];

  // Show skeleton while loading
  if (isLoading || !puzzleConfig) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
      >
        <div className={styles.container}>
          <p className={gameStyles.greeting}>
            {getGameMessage(gameConfig.id, "not_started")}
          </p>
          <SpettriSkeleton />
        </div>
      </GameShell>
    );
  }

  // Show error state
  if (sessionError && !sessionState) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
      >
        <div className={styles.container}>
          <p className={gameStyles.greeting}>
            {ERROR_MESSAGES.GAME_LOAD_ERROR}
          </p>
        </div>
      </GameShell>
    );
  }

  // Get titles to display - prefer solutions (when game over), then use revealed titles
  const displayTitles = solutions 
    ? [solutions[0].title, solutions[1].title] 
    : revealedTitles;
  const aiSelectionEnabled = gameOver && !isPreview;
  const isAIOnboardingReady = gameOver && showEndContent && !isPreview;

  return (
    <GameShell
      gameConfig={gameConfig}
      actions={headerActions}
      sponsorship={sponsorship}
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      modalTitle={modalTitle}
      modalContent={modalContent}
      key={sessionState?.session_id || effectiveDate}
      resultsPresentationReady={showEndContent && !isAnimatingReveal}
      showCelebration={justWon}
      isCompleted={gameOver}
      showNavigation={showNavigation}
      showGameHeader={showGameHeader}
      mobileGameplayAd={mobileGameplayAd}
    >
      <div className={styles.container}>
        <DndContext 
          sensors={sensors} 
          collisionDetection={collisionDetection} 
          onDragStart={onDragStart} 
          onDragEnd={onDragEnd}
        >
          <DragOverlay>
            {draggingItem ? (
              <div className={styles.spectrumItem} style={{ touchAction: 'none' }}>
                {draggingItem.item}
              </div>
            ) : null}
          </DragOverlay>

          <SnackAlert ref={snackAlertRef} />
          
          {!gameOver && (
            <p className={gameStyles.greeting}>
              {getGameMessage(gameConfig.id, "not_started")}
            </p>
          )}

          <AIHelperTrigger
            puzzleInstanceId={puzzleInstanceId}
            gameSlug={gameConfig.id}
            enabled={aiSelectionEnabled}
            onboardingReady={isAIOnboardingReady}
            coachmarkSelector='[data-ai-component="spectrum_item"]'
          >
          <div className={styles.spectraContainer}>
            <div className={styles.spectrumWrapper}>
              <div className={styles.spectrumRowContainer}>
                <div className={styles.directionIndicator}>
                  <div className={styles.arrow}>↑</div>
                  <div className={styles.directionText}>crescente</div>
                </div>
                <SpectrumColumn
                  items={spectrum0}
                  spectrumIdx={0}
                  spectrumTitle={displayTitles?.[0]}
                  isFixed={isFixed}
                  isCorrect={isCorrect}
                  feedback={feedback}
                  disabled={completed || gameOver || isAnimatingReveal}
                  flippingItems={flippingItems}
                  swappedItems={swappedItems}
                  revealedItems={revealedItems}
                  showingSolution={showingSolution}
                />
                <div className={styles.spacer}></div>
              </div>
              {displayTitles && !isAnimatingReveal && (
                <h3 className={styles.spectrumTitle}><span data-ai-component="spectrum_title" data-ai-title={displayTitles[0]}>{displayTitles[0]}</span></h3>
              )}
            </div>
            <div className={styles.spectrumWrapper}>
              <div className={styles.spectrumRowContainer}>
                <div className={styles.directionIndicator}>
                  <div className={styles.arrow}>↑</div>
                  <div className={styles.directionText}>crescente</div>
                </div>
                <SpectrumColumn
                  items={spectrum1}
                  spectrumIdx={1}
                  spectrumTitle={displayTitles?.[1]}
                  isFixed={isFixed}
                  isCorrect={isCorrect}
                  feedback={feedback}
                  disabled={completed || gameOver || isAnimatingReveal}
                  flippingItems={flippingItems}
                  swappedItems={swappedItems}
                  revealedItems={revealedItems}
                  showingSolution={showingSolution}
                />
                <div className={styles.spacer}></div>
              </div>
              {displayTitles && !isAnimatingReveal && (
                <h3 className={styles.spectrumTitle}><span data-ai-component="spectrum_title" data-ai-title={displayTitles[1]}>{displayTitles[1]}</span></h3>
              )}
            </div>
          </div>
          </AIHelperTrigger>

          {/* Lives display - only during gameplay */}
          {!gameOver && (
            <LivesDisplay lives={lives} maxLives={5} />
          )}

          {/* Submit button - only during gameplay */}
          {!completed && !gameOver && (
            <div className={styles.submitContainer} ref={gameplayControlsRef}>
              <Button 
                primary 
                onClick={handleSubmit} 
                disabled={isSubmitPending || submitCooldown || isAnimatingReveal || !spectrum0.length || !spectrum1.length}
                loading={isSubmitPending}
                loadingText="Invio"
              >
                Invia
              </Button>
            </div>
          )}

          {/* Game end section - shows inline when game ends */}
          {gameOver && showEndContent && (
            isPreview && !previewSponsorship ? (
              <div ref={setEndSectionAnchor} className={styles.previewEndSection}>
                <Button primary onClick={handleClosePreview}>
                  {previewEndLabel || "Chiudi anteprima"}
                </Button>
              </div>
            ) : (
              <GameEndSection
                ref={setEndSectionAnchor}
                won={completed}
                justWon={justWon}
                gameConfig={gameConfig}
                sessionState={sessionState}
                puzzleConfig={puzzleConfig}
                justCompleted={justWon || justLost}
                date={effectiveDate}
                shouldTrack={shouldTrack}
                sponsorship={sponsorship}
                previewMode={isPreview}
                message={completed ? 'Hai ordinato correttamente le progressioni! 🎉' : 'Peccato! Riprova domani. 💪'}
                snackAlertRef={snackAlertRef}
              />
            )
          )}
        </DndContext>
      </div>
    </GameShell>
  );
};

/**
 * SpectrumColumn renders items in DISPLAY ORDER (reversed from backend order).
 * Takes items in backend order and reverses them so highest is at top.
 */
const SpectrumColumn = ({ items, spectrumIdx, spectrumTitle, isFixed, isCorrect, feedback, disabled, flippingItems = new Set(), swappedItems = new Set(), revealedItems = new Set(), showingSolution = false }) => {
  // Reverse items for display: backend[0]=lowest becomes display[4]=bottom
  const displayItems = [...items].reverse();
  
  return (
    <div className={styles.spectrumColumn}>
      {displayItems.map((item, displayIdx) => {
        // Convert display index to backend index
        const backendIdx = items.length - 1 - displayIdx;
        
        const fixedItem = isFixed(spectrumIdx, backendIdx);
        const correctItem = isCorrect(spectrumIdx, backendIdx);
        const isFlipping = flippingItems.has(`${spectrumIdx}-${backendIdx}`);
        const isSwapped = swappedItems.has(`${spectrumIdx}-${backendIdx}`);
        const isRevealed = revealedItems.has(`${spectrumIdx}-${backendIdx}`);
        
        // Get feedback for this backend position
        const feedbackItem = feedback?.[spectrumIdx]?.[backendIdx];
        // Don't show incorrect feedback when showing final solution or after item was revealed
        const hasIncorrectFeedback = !showingSolution && !isRevealed && feedbackItem && !feedbackItem.correct;
        
        // Build class list
        const classes = [styles.spectrumItem];
        if (fixedItem) {
          classes.push(styles.spectrumItemFixed);
          classes.push(styles.spectrumItemCorrect);
        } else if (correctItem || showingSolution || isRevealed) {
          // When showing solution or item was revealed during animation, show as correct
          classes.push(styles.spectrumItemCorrect);
        } else if (hasIncorrectFeedback) {
          classes.push(styles.spectrumItemIncorrect);
        }
        if (disabled) {
          classes.push(styles.spectrumItemDisabled);
        }
        if (isSwapped) {
          classes.push(styles.spectrumItemSwapFlash);
        }
        if (isFlipping) {
          classes.push(styles.spectrumItemFlipping);
        }
        
        return (
          <DraggableItem
            key={`${spectrumIdx}-${displayIdx}`}
            item={item}
            spectrumIdx={spectrumIdx}
            displayIdx={displayIdx}
            backendIdx={backendIdx}
            isFixed={fixedItem}
            isCorrect={correctItem || showingSolution || isRevealed}
            className={classes.join(' ')}
            disabled={disabled}
            aiAttributes={{ 'data-ai-component': 'spectrum_item', 'data-ai-item': item, 'data-ai-title': spectrumTitle }}
          />
        );
      })}
    </div>
  );
};

const DraggableItem = ({ item, spectrumIdx, displayIdx, backendIdx, isFixed, isCorrect, className, disabled, aiAttributes = {} }) => {
  const dragId = `spectrum-${spectrumIdx}-display-${displayIdx}`;
  
  const canDrag = !isFixed && !disabled && !isCorrect;
  
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: dragId,
    data: { item, spectrumIdx, displayIdx },
    disabled: !canDrag
  });

  const { setNodeRef: setDropRef } = useDroppable({
    id: dragId,
    data: { spectrumIdx, displayIdx },
    disabled: disabled || isCorrect
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    cursor: 'grabbing',
    opacity: isDragging ? 0 : 1,
    touchAction: 'none'
  } : {
    cursor: canDrag ? 'grab' : 'default',
    touchAction: canDrag ? 'none' : undefined
  };

  const setNodeRef = (node) => {
    setDragRef(node);
    setDropRef(node);
  };

  return (
    <div
      ref={setNodeRef}
      className={className}
      style={style}
      data-position={(isFixed || isCorrect) ? backendIdx : undefined}
      {...(canDrag ? { ...attributes, ...listeners } : {})}
    >
      <span {...aiAttributes}>{item}</span>
    </div>
  );
};

const LEGACY_DEMO_PHASES = (() => {
  const correctSpectrum0 = ['vitale', 'fondamentale', 'rilevante', 'secondario', 'trascurabile'];
  const correctSpectrum1 = ['frastuono', 'chiasso', 'parlato', 'sussurro', 'silenzio'];

  return [
    {
      spectrum0: ['rilevante', 'fondamentale', 'vitale', 'secondario', 'trascurabile'],
      spectrum1: ['frastuono', 'parlato', 'sussurro', 'chiasso', 'silenzio'],
      states0: [null, 'correct', null, null, null],
      states1: [null, null, null, null, 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: ['rilevante', 'fondamentale', 'vitale', 'secondario', 'trascurabile'],
      spectrum1: ['frastuono', 'sussurro', 'parlato', 'chiasso', 'silenzio'],
      states0: [null, 'correct', null, null, null],
      states1: [null, null, null, null, 'correct'],
      swaps0: [],
      swaps1: [1, 2],
    },
    {
      spectrum0: ['rilevante', 'fondamentale', 'frastuono', 'secondario', 'trascurabile'],
      spectrum1: ['vitale', 'sussurro', 'parlato', 'chiasso', 'silenzio'],
      states0: [null, 'correct', null, null, null],
      states1: [null, null, null, null, 'correct'],
      swaps0: [2],
      swaps1: [0],
    },
    {
      spectrum0: ['rilevante', 'fondamentale', 'frastuono', 'secondario', 'trascurabile'],
      spectrum1: ['vitale', 'sussurro', 'parlato', 'chiasso', 'silenzio'],
      states0: ['incorrect', 'correct', 'incorrect', 'correct', 'correct'],
      states1: ['incorrect', 'incorrect', 'correct', 'incorrect', 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: ['vitale', 'fondamentale', 'frastuono', 'secondario', 'trascurabile'],
      spectrum1: ['rilevante', 'sussurro', 'parlato', 'chiasso', 'silenzio'],
      states0: ['incorrect', 'correct', 'incorrect', 'correct', 'correct'],
      states1: ['incorrect', 'incorrect', 'correct', 'incorrect', 'correct'],
      swaps0: [0],
      swaps1: [0],
    },
    {
      spectrum0: ['vitale', 'fondamentale', 'frastuono', 'secondario', 'trascurabile'],
      spectrum1: ['rilevante', 'sussurro', 'parlato', 'chiasso', 'silenzio'],
      states0: ['correct', 'correct', 'incorrect', 'correct', 'correct'],
      states1: ['incorrect', 'incorrect', 'correct', 'incorrect', 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: ['vitale', 'fondamentale', 'rilevante', 'secondario', 'trascurabile'],
      spectrum1: ['frastuono', 'sussurro', 'parlato', 'chiasso', 'silenzio'],
      states0: ['correct', 'correct', 'incorrect', 'correct', 'correct'],
      states1: ['incorrect', 'incorrect', 'correct', 'incorrect', 'correct'],
      swaps0: [2],
      swaps1: [0],
    },
    {
      spectrum0: ['vitale', 'fondamentale', 'rilevante', 'secondario', 'trascurabile'],
      spectrum1: ['frastuono', 'sussurro', 'parlato', 'chiasso', 'silenzio'],
      states0: ['correct', 'correct', 'correct', 'correct', 'correct'],
      states1: ['correct', 'incorrect', 'correct', 'incorrect', 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: ['vitale', 'fondamentale', 'rilevante', 'secondario', 'trascurabile'],
      spectrum1: ['frastuono', 'chiasso', 'parlato', 'sussurro', 'silenzio'],
      states0: ['correct', 'correct', 'correct', 'correct', 'correct'],
      states1: ['correct', 'incorrect', 'correct', 'incorrect', 'correct'],
      swaps0: [],
      swaps1: [1, 3],
    },
    {
      spectrum0: correctSpectrum0,
      spectrum1: correctSpectrum1,
      states0: ['correct', 'correct', 'correct', 'correct', 'correct'],
      states1: ['correct', 'correct', 'correct', 'correct', 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: correctSpectrum0,
      spectrum1: correctSpectrum1,
      states0: ['correct', 'correct', 'correct', 'correct', 'correct'],
      states1: ['correct', 'correct', 'correct', 'correct', 'correct'],
      swaps0: [],
      swaps1: [],
    },
  ];
})();

const FOUR_ITEM_DEMO_PHASES = (() => {
  const correctSpectrum0 = ['caldo', 'tiepido', 'freddo', 'gelido'];
  const correctSpectrum1 = ['notte', 'sera', 'pomeriggio', 'mattina'];

  return [
    {
      spectrum0: ['tiepido', 'caldo', 'freddo', 'gelido'],
      spectrum1: ['notte', 'pomeriggio', 'sera', 'mattina'],
      states0: [null, null, 'correct', null],
      states1: [null, null, null, 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: ['tiepido', 'caldo', 'freddo', 'gelido'],
      spectrum1: ['notte', 'sera', 'pomeriggio', 'mattina'],
      states0: [null, null, 'correct', null],
      states1: [null, null, null, 'correct'],
      swaps0: [],
      swaps1: [1, 2],
    },
    {
      spectrum0: ['notte', 'caldo', 'freddo', 'gelido'],
      spectrum1: ['tiepido', 'sera', 'pomeriggio', 'mattina'],
      states0: [null, null, 'correct', null],
      states1: [null, null, null, 'correct'],
      swaps0: [0],
      swaps1: [0],
    },
    {
      spectrum0: ['notte', 'caldo', 'freddo', 'gelido'],
      spectrum1: ['tiepido', 'sera', 'pomeriggio', 'mattina'],
      states0: ['incorrect', 'incorrect', 'correct', 'correct'],
      states1: ['incorrect', 'correct', 'correct', 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: ['caldo', 'notte', 'freddo', 'gelido'],
      spectrum1: ['tiepido', 'sera', 'pomeriggio', 'mattina'],
      states0: ['incorrect', 'incorrect', 'correct', 'correct'],
      states1: ['incorrect', 'correct', 'correct', 'correct'],
      swaps0: [0, 1],
      swaps1: [],
    },
    {
      spectrum0: ['caldo', 'notte', 'freddo', 'gelido'],
      spectrum1: ['tiepido', 'sera', 'pomeriggio', 'mattina'],
      states0: ['correct', 'incorrect', 'correct', 'correct'],
      states1: ['incorrect', 'correct', 'correct', 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: ['caldo', 'tiepido', 'freddo', 'gelido'],
      spectrum1: ['notte', 'sera', 'pomeriggio', 'mattina'],
      states0: ['correct', 'incorrect', 'correct', 'correct'],
      states1: ['incorrect', 'correct', 'correct', 'correct'],
      swaps0: [1],
      swaps1: [0],
    },
    {
      spectrum0: ['caldo', 'tiepido', 'freddo', 'gelido'],
      spectrum1: ['notte', 'sera', 'pomeriggio', 'mattina'],
      states0: ['correct', 'correct', 'correct', 'correct'],
      states1: ['correct', 'correct', 'correct', 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: correctSpectrum0,
      spectrum1: correctSpectrum1,
      states0: ['correct', 'correct', 'correct', 'correct'],
      states1: ['correct', 'correct', 'correct', 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: correctSpectrum0,
      spectrum1: correctSpectrum1,
      states0: ['correct', 'correct', 'correct', 'correct'],
      states1: ['correct', 'correct', 'correct', 'correct'],
      swaps0: [],
      swaps1: [],
    },
    {
      spectrum0: correctSpectrum0,
      spectrum1: correctSpectrum1,
      states0: ['correct', 'correct', 'correct', 'correct'],
      states1: ['correct', 'correct', 'correct', 'correct'],
      swaps0: [],
      swaps1: [],
    },
  ];
})();

const DEMO_PHASE_DURATIONS = [
  2000, 1600, 1600, 1500, 1600, 1500, 1600, 1500, 1600, 1600, 2500,
];

const MiniSpettriDemo = ({ itemsPerSpectrum = 5 }) => {
  const [phase, setPhase] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setPhase((p) => (p + 1) % DEMO_PHASE_DURATIONS.length);
    }, DEMO_PHASE_DURATIONS[phase]);
    
    return () => clearTimeout(timer);
  }, [phase]);

  const phases = itemsPerSpectrum === 4 ? FOUR_ITEM_DEMO_PHASES : LEGACY_DEMO_PHASES;
  const { spectrum0, spectrum1, states0, states1, swaps0, swaps1 } = phases[phase];

  return (
    <div className={styles.miniDemo}>
      <div className={styles.miniDemoColumn}>
        {spectrum0.map((item, i) => (
          <div 
            key={`s0-${i}`}
            className={`${styles.spectrumItem} ${
              states0[i] === 'correct' ? styles.spectrumItemCorrect : 
              states0[i] === 'incorrect' ? styles.spectrumItemIncorrect : ''
            } ${swaps0.includes(i) ? styles.spectrumItemSwapFlash : ''}`}
            data-position={states0[i] === 'correct' ? (itemsPerSpectrum - 1 - i) : undefined}
          >
            {item}
          </div>
        ))}
      </div>
      <div className={styles.miniDemoColumn}>
        {spectrum1.map((item, i) => (
          <div 
            key={`s1-${i}`}
            className={`${styles.spectrumItem} ${
              states1[i] === 'correct' ? styles.spectrumItemCorrect : 
              states1[i] === 'incorrect' ? styles.spectrumItemIncorrect : ''
            } ${swaps1.includes(i) ? styles.spectrumItemSwapFlash : ''}`}
            data-position={states1[i] === 'correct' ? (itemsPerSpectrum - 1 - i) : undefined}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
};

const Instructions = ({ itemsPerSpectrum = 5 }) => {
  return (
    <div className={`${modalStyles.modalContent} ${modalStyles.instructions}`}>
      <p>Ricostruisci le due progressioni.</p>
      <MiniSpettriDemo itemsPerSpectrum={itemsPerSpectrum} />
      <ul>
        <li>Assegna le parole alla progressione corretta.</li>
        <li>Una parola di ciascuna progressione, indicata dal carattere grassetto e una barra arancione, è già assegnata e la sua posizione non può essere cambiata.</li>
        <li>Metti in ordine crescente le parole di ogni progressione, dal basso verso l'alto, trascinandole nella posizione corretta.</li>
        <li>Premi <b>Invia</b> per controllare la tua risposta.</li>
        <li>Le parole corrette sono indicate dal carattere grassetto e una barra arancione; quelle sbagliate sono rosse.</li>
        <li>Se sbagli, perdi una vita. <b>Hai 5 vite</b> a disposizione per completare il gioco!</li>
      </ul>
      <p>Se sei bloccato, puoi usare uno degli aiutini <FaIcon className="fa fa-medkit"  /> a tua disposizione in alto a destra.</p>
    </div>
  );
};

export default SpettriGame;

const Aiutini = ({
  revealUsed,
  arrangeUsed,
  onReveal,
  onArrange,
  disabled,
  hintCosts = {},
  hintAffordability = {},
  loadingHint = null,
  errorMessage = "",
  isSignedIn = true,
  hasFreeHints = false,
  onAuthRedirect = null,
  hasEverAuthenticated = false,
  isPreview = false,
  balance = 0,
}) => {
  const renderButtonContent = ({ alreadyUsed = false } = {}) => {
    if (isPreview) {
      return 'Ricevi aiutino';
    }
    if (!isSignedIn && !hasFreeHints) {
      return hasEverAuthenticated ? 'Accedi' : 'Registrati';
    }
    if (alreadyUsed) {
      return 'Già usato';
    }
    return 'Usa aiutino';
  };
  const shouldShowCostChip = (cost) => !isPreview && (hasFreeHints || (Number.isFinite(Number(cost)) && Number(cost) > 0));
  const disabledHintCount = getDisabledHintCount({
    isSignedIn,
    isPreview,
    hintAffordability,
    excludedHintKeys: [
      ...(revealUsed ? ['reveal_titles'] : []),
      ...(arrangeUsed ? ['arrange_columns'] : []),
    ],
  });
  const shouldShowInsufficientBalanceState = isSignedIn && (
    errorMessage === INSUFFICIENT_HINT_BALANCE_MESSAGE
    || disabledHintCount > 0
  );
  const shouldShowGenericError = Boolean(errorMessage) && !shouldShowInsufficientBalanceState;

  return (
    <div className={modalStyles.modalContent}>
      {shouldShowInsufficientBalanceState && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <div className={styles.aiutiniInlineErrorContent}>
            <div>
              {getDisabledHintsMessage({
                balance,
                disabledHintCount: Math.max(disabledHintCount, 1),
              })}
            </div>
            <div className={styles.aiutiniInlineErrorCard}>
              <RewardedGettonciniCard
                sourceSurface="aiutini_modal"
                gameSlug="spettri"
                compact
              />
            </div>
          </div>
        </div>
      )}
      {shouldShowGenericError && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <span>
            {errorMessage}
          </span>
        </div>
      )}
      {(!isSignedIn || !shouldShowInsufficientBalanceState) && (
        <p className={styles.aiutinoPreamble}>
          {!isSignedIn && !isPreview && !hasFreeHints ? (
            <>
              Per usare gli aiutini hai bisogno di gettoncini, che puoi guadagnare con un account gratuito.{' '}
              <span className={styles.authLink} onClick={onAuthRedirect}>
                {hasEverAuthenticated ? 'Accedi' : 'Registrati'} <FaIcon className="fa-solid fa-arrow-right"  />
              </span>
            </>
          ) : 'Se sei bloccato, puoi usare uno di questi aiutini. Ogni aiutino può essere usato una sola volta.'}
        </p>
      )}
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-tag"  /></span>
            <span className={styles.aiutinoTitle}>Rivela temi</span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              onClick={onReveal}
              disabled={disabled || revealUsed || loadingHint !== null || (isSignedIn && !revealUsed && !hintAffordability.reveal_titles)}
              className={[
                styles.aiutinoActionRight,
                isSignedIn && !revealUsed && !hintAffordability.reveal_titles ? styles.aiutinoUnavailable : "",
              ].filter(Boolean).join(" ")}
              loading={loadingHint === 'reveal_titles'}
              aria-disabled={isSignedIn && !revealUsed && !hintAffordability.reveal_titles}
            >
              {renderButtonContent({ alreadyUsed: revealUsed })}
            </Button>
            {shouldShowCostChip(hintCosts.reveal_titles) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.reveal_titles} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Mostra i temi delle due progressioni.</span>
      </div>

      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-arrows-left-right"  /></span>
            <span className={styles.aiutinoTitle}>Riordina colonne</span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              onClick={onArrange}
              disabled={disabled || arrangeUsed || loadingHint !== null || (isSignedIn && !arrangeUsed && !hintAffordability.arrange_columns)}
              className={[
                styles.aiutinoActionRight,
                isSignedIn && !arrangeUsed && !hintAffordability.arrange_columns ? styles.aiutinoUnavailable : "",
              ].filter(Boolean).join(" ")}
              loading={loadingHint === 'arrange_columns'}
              aria-disabled={isSignedIn && !arrangeUsed && !hintAffordability.arrange_columns}
            >
              {renderButtonContent({ alreadyUsed: arrangeUsed })}
            </Button>
            {shouldShowCostChip(hintCosts.arrange_columns) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.arrange_columns} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Sposta gli elementi sbagliati nella colonna corretta (ordine casuale).</span>
      </div>
    </div>
  );
};
